import { inject, injectable } from 'inversify';

import { IMonitoringService } from '@desygner/ui-core-monitoring';
import {
  FontManager,
  TYPES as PROJECT_SERVICE_TYPES,
  Page,
  ProjectStoreInjector,
  ProjectsFacade,
} from '@desygner/ui-features-projects';

import { TYPES } from '../../inversify.types';
import { IConfigStoreInjector, IViewerStoreInjector } from '../../store';
import { SVGManager } from './svg-manager';
import { VideoManager } from './video-manager';

export interface PageManagerProps {
  pageId: number;
  page: Page;
}

@injectable()
export class PageManager {
  private processedPageIds = new Set<number | string>();
  private processingPages = new Map<number | string, Promise<void>>();

  constructor(
    @inject(PROJECT_SERVICE_TYPES.FontManager)
    private readonly fontManager: FontManager,
    @inject(TYPES.Viewer.SVGManager)
    private readonly svgManager: SVGManager,
    @inject(TYPES.Viewer.VideoManager)
    private readonly videoManager: VideoManager,
    @inject(PROJECT_SERVICE_TYPES.ProjectsFacade)
    private readonly projectsFacade: ProjectsFacade,
    @inject(PROJECT_SERVICE_TYPES.ProjectsStore)
    private readonly projectsStore: ProjectStoreInjector,
    @inject(TYPES.Store.Viewer)
    private readonly viewerStore: IViewerStoreInjector,
    @inject(TYPES.Store.Config)
    private readonly configStore: IConfigStoreInjector,
    @inject(TYPES.MonitoringService)
    private readonly monitoringService: IMonitoringService
  ) {
    const staticUrl = this.configStore.getState().resources.staticUrl;
    const fontPathPrefix = this.configStore.getState().fontPathPrefix;

    this.fontManager.enableObfuscation(staticUrl, fontPathPrefix);
  }

  private async processPage({ pageId, page }: PageManagerProps): Promise<void> {
    const environment = this.configStore.getState().environment;
    const pageDimensionWidth =
      this.viewerStore.getState().pageDimension?.width || window.innerWidth;
    const project = this.projectsFacade.getProject();

    this.setInitialPage(pageId, page);

    try {
      const loadedPage = await this.fetchPageData(pageId, project?.is_public || false);

      const svg = await this.prepareSvgContent(loadedPage.content, environment);

      if (!svg) {
        throw new Error(`SVG content for page ${pageId} could not be prepared.`);
      }

      const processedSvg = await this.processPageContent(svg, environment);
      const processedSvgWithMedia = this.videoManager.embedNonYoutubeVideos(processedSvg);

      await this.loadAndProcessFonts(processedSvgWithMedia);

      const pageContent = this.generatePageContent(processedSvgWithMedia, pageDimensionWidth);

      this.setFinalPage(pageId, pageContent);

      this.processedPageIds.add(pageId);
    } catch (error) {
      console.error(`Critical error loading page ${pageId}, removing from processed set:`, error);

      this.monitoringService.logException(error as Error);
      throw error;
    } finally {
      this.processingPages.delete(pageId);
    }
  }

  public loadPageContent(props: PageManagerProps): Promise<void> {
    const { pageId } = props;

    if (this.processedPageIds.has(pageId)) {
      return Promise.resolve();
    }

    const existingPromise = this.processingPages.get(pageId);

    if (existingPromise) {
      return existingPromise;
    }

    const newPromise = this.processPage(props);

    this.processingPages.set(pageId, newPromise);
    return newPromise;
  }

  private async prepareSvgContent(content: string, environment: string) {
    const parsedSVGContent = this.svgManager.createSvgElement(content || '<svg></svg>');
    const svgWithViewBox = this.svgManager.setSVGViewbox(parsedSVGContent);

    try {
      return await this.svgManager.removeInvalidImagesFromSVG(
        svgWithViewBox,
        environment === 'qa' || environment === 'develop'
      );
    } catch (error) {
      console.warn('Failed to remove invalid images from SVG, using original content:', error);

      this.monitoringService.logException(error as Error);
      return svgWithViewBox;
    }
  }

  private async processPageContent(svg: HTMLElement, environment: string) {
    const processedSvg = await this.svgManager
      .processFlippedImages(svg, environment === 'qa' || environment === 'develop')
      .catch((error) => {
        console.warn('Failed to process flipped images, using original SVG:', error);

        this.monitoringService.logException(error as Error);
        return svg;
      });

    return processedSvg;
  }

  private async loadAndProcessFonts(svg: HTMLElement) {
    try {
      await this.extractAndLoadFonts(svg);
    } catch (error) {
      console.warn('Failed to load fonts, continuing without font loading:', error);

      this.monitoringService.logException(error as Error);
    }
  }

  private generatePageContent(svg: HTMLElement, pageDimensionWidth: number) {
    const whitelistAttributes = ['custom-font-id'];
    const sanitizedHtml = this.svgManager.sanitizeDOM(svg.outerHTML, whitelistAttributes);
    const { height: viewBoxHeight, width: viewBoxWidth } = this.svgManager.getSVGDimensions(svg);
    const { height, width } = this.svgManager.normalizeDimension({
      originalHeight: viewBoxHeight,
      originalWidth: viewBoxWidth,
      expectedWidth: pageDimensionWidth,
    });

    return { content: sanitizedHtml, width, height };
  }

  private async fetchPageData(pageId: number, isPublic: boolean) {
    return isPublic
      ? await this.projectsFacade.loadPublicPage(pageId)
      : await this.projectsFacade.loadPage(pageId);
  }

  private setInitialPage(pageId: number, page: Page) {
    const initialPage: Page = {
      content: '',
      id: pageId,
      height: page.height,
      width: page.width,
    };

    this.projectsStore.getState().pages.setPage(initialPage);
  }

  private setFinalPage(
    pageId: number,
    pageContent: { content: string; width: number; height: number }
  ) {
    const finalPage: Page = { ...pageContent, id: pageId };

    this.projectsStore.getState().pages.setPage(finalPage);
  }

  private async extractAndLoadFonts(svg: HTMLElement) {
    const fontsToLoad = this.fontManager.extractFontsFromSVG(svg);

    await this.fontManager.loadFonts(fontsToLoad);
  }

  public clearProcessedPageId(pageId: number | string): void {
    this.processedPageIds.delete(pageId);
  }

  public clearAllProcessedPageIds(): void {
    this.processedPageIds.clear();
    this.processingPages.clear();
  }
}
